import type { VoidFunctionComponent } from 'react';

import { CodeViewer } from '../../../../generic/CodeViewer/CodeViewer';

import Empty from './CodeMirrorEmpty';
import CodeMirrorExpired from './CodeMirrorExpired';
import Loading from './CodeMirrorLoader';

import styles from './SnippetsDrawerCode.module.less';

type Props = {
  /**
   * If we are fetching the contents of a file right now.
   */
  isLoading: boolean;
  /**
   * The path the user has selected in the file tree.
   */
  selectedPath: string | null;
  /**
   * When the 1st-party file contents were detected.
   */
  detectedAt?: Date;
  /**
   * When the 1st-party file contents will expire.
   */
  expiresAt?: Date;
  /**
   * Contents of the file to display.
   */
  fileContents: string;
  /**
   * Zero-based line numbers of the snippet match, highlighted in the viewer.
   */
  highlightedLines: number[];
};

/**
 * Renders the actual CodeViewer component, with fallbacks for empty and loading states.
 */
const CodeMirror: VoidFunctionComponent<Props> = ({
  isLoading,
  selectedPath,
  detectedAt,
  expiresAt,
  fileContents,
  highlightedLines,
}) => {
  //  If there is no selected path, we display a fallback "select a file" message
  const hasSelectedPath = !!selectedPath;

  if (!hasSelectedPath) {
    return (
      <Empty>
        Select a file from the tree on the left to view snippet matches.
      </Empty>
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  if (detectedAt && expiresAt && expiresAt <= new Date()) {
    return <CodeMirrorExpired detectedAt={detectedAt} expiresAt={expiresAt} />;
  }

  if (!fileContents) {
    return (
      <Empty>
        Code cannot be displayed at this time.
      </Empty>
    );
  }

  return (
    <CodeViewer
      className={styles.codeMirror}
      fileContents={fileContents}
      fileName={selectedPath}
      highlightedLines={highlightedLines.map(line => ({ line, className: styles.highlightedLine }))}
      scrollToFirstHighlight
    />
  );
};

export default CodeMirror;
